import { numberFormatted } from '../../../../utils/fns'

import debounce from 'lodash/debounce'
import { getFormValid, resultActions } from '../slices'
import {
  ANGLE_SKEW_TYPE,
  ANGLE_SLOPE_TYPE,
  BUILDING_CODE,
  CARRIED_MEMBER,
  CONCEALED_TYPE,
  CONNECTION_TYPE,
  HANGER_TYPE,
  LEDGER_TYPE,
  MEMBER_TYPE,
  OPEN_CLOSED_TYPE,
  SLOPED_DOWN_TYPE,
} from '../../common/constants'
import {
  getFloorTrussValue,
  isCarryingMember,
  translateNominal,
  isCalculationAction,
  toAbsoluteValue,
  deleteUndefinedField,
  isMemberTypeLedger,
  isHangerTypeConcealed,
  isJoistConnectionType,
  isMultiTrussConnectionType,
} from '../../common/fns'
import { getCalcInputState } from '../../../../utils/selectors'
import { selectMembersState } from '../selectors'
import { ijoistUtils } from '../../common/utils/ijoistUtils'

// Based on Joist or Truss connection
const FLUSH_OPTION = {
  TOP: 'TOP',
  BOTTOM: 'BOTTOM',
}

const FILTERS_QUERY = {
  depth: 0,
  model: '',
  series: '',
  webStiffeners: 0,
  width: 0,
}

const buildDesignInformationsPayload = (jobSettings) => {
  return {
    downloadDurationType: jobSettings.downloadDurationType,
    upliftLoadDurationType: jobSettings.upliftLoadDurationType,
  }
}

const buildTopFlangeOptionsPayload = (hangerOptions) => {
  const { topFlangeOpenClosedAngle, topFlangeSlopedDownAngle, topFlangeOffset } = hangerOptions

  return {
    topFlangeOpenClosedAngle,
    topFlangeOpenClosedType:
      Number(topFlangeOpenClosedAngle) > 0
        ? OPEN_CLOSED_TYPE.OPEN
        : Number(topFlangeOpenClosedAngle) < 0
        ? OPEN_CLOSED_TYPE.CLOSED
        : OPEN_CLOSED_TYPE.NONE,
    topFlangeSlopedDownAngle,
    topFlangeSlopedDownType:
      Number(topFlangeSlopedDownAngle) > 0
        ? SLOPED_DOWN_TYPE.RIGHT
        : Number(topFlangeSlopedDownAngle) < 0
        ? SLOPED_DOWN_TYPE.LEFT
        : SLOPED_DOWN_TYPE.NONE,
    topFlangeOffset,
  }
}

const buildAnglePayload = (data) => {
  const { skewAngle, slopeAngle } = data

  return {
    skewAngle,
    skewType:
      Number(skewAngle) > 0
        ? ANGLE_SKEW_TYPE.RIGHT
        : Number(skewAngle) < 0
        ? ANGLE_SKEW_TYPE.LEFT
        : ANGLE_SKEW_TYPE.NONE,
    slopeAngle,
    slopeType:
      Number(slopeAngle) > 0
        ? ANGLE_SLOPE_TYPE.DOWN
        : Number(slopeAngle) < 0
        ? ANGLE_SLOPE_TYPE.UP
        : ANGLE_SLOPE_TYPE.NONE,
  }
}

const buildCarryingMemberPayload = (data) => {
  // eslint-disable-next-line no-unused-vars
  const { ijoistInputType, isRough, memberID, model, manufacturer, series, thickness, ...rest } =
    data

  return rest
}

const isSkewNone = (skewAngle) => skewAngle === 0

const buildCarriedMemberPayload = (data) => {
  const { material, width, depth, ply, load, uplift } = data

  return {
    material,
    width,
    depth,
    ply,
    loads: {
      load,
      uplift,
    },
  }
}

const buildCarriedMemberAnglePayload = (name, data) => {
  const memberAngle = {
    skewAngle: data.skewAngle,
    slopeAngle: data.slopeAngle,
  }
  const angle = buildAnglePayload(memberAngle)

  if (name === CARRIED_MEMBER.LEFT_HIP) {
    angle.skewType = isSkewNone(angle.skewAngle) ? ANGLE_SKEW_TYPE.NONE : ANGLE_SKEW_TYPE.LEFT
  } else if (name === CARRIED_MEMBER.RIGHT_HIP) {
    angle.skewType = isSkewNone(angle.skewAngle) ? ANGLE_SKEW_TYPE.NONE : ANGLE_SKEW_TYPE.RIGHT
  }

  return angle
}

const getMemberSetByConnectionType = (members, connectionType = CONNECTION_TYPE.JOIST) => {
  const { header, joist, girder, truss, jack, leftHip, rightHip } = members

  const memberConnectionType = {
    [CONNECTION_TYPE.JOIST]: { header, joist },
    [CONNECTION_TYPE.TRUSS]: { girder, truss },
    [CONNECTION_TYPE.MULTI_TRUSS]: { girder, jack, leftHip, rightHip },
  }

  return memberConnectionType[connectionType]
}

const updateMemberData = (member, options) => {
  const { type, width, depth, isRough } = member

  switch (type) {
    case MEMBER_TYPE.SOLID_SAWN:
    case MEMBER_TYPE.LEDGER: {
      const nominalValue = translateNominal(width, depth, isRough)

      return { ...member, ...nominalValue }
    }
    case MEMBER_TYPE.FLOOR_TRUSS: {
      if (options.isCarryingMember) {
        const floorTrussValue = getFloorTrussValue(width)

        return { ...member, ...floorTrussValue }
      } else return { ...member }
    }
    case MEMBER_TYPE.NAILER: {
      const swampedValue = { width: depth, depth: width }

      return { ...member, ...swampedValue }
    }
    case MEMBER_TYPE.IJOIST: {
      const material = ijoistUtils.convertManualMaterial(member)

      return { ...member, material }
    }
    default: {
      return { ...member }
    }
  }
}

const buildMemberPayload = (memberData, options) => {
  const memberSet = getMemberSetByConnectionType(memberData, options.connectionType)

  const payload = Object.entries(memberSet).reduce(
    (result, [memberName, memberData]) => {
      const isCarrying = isCarryingMember(memberName)
      const updatedMemberData = updateMemberData(memberData, {
        isCarryingMember: isCarrying,
      })

      if (isCarrying) result.carryingMember = buildCarryingMemberPayload(updatedMemberData)
      else {
        const carriedMemberPayload = buildCarriedMemberPayload(updatedMemberData)

        const carriedMemberAngle = options.isMultiTrussConnection
          ? buildCarriedMemberAnglePayload(memberName, updatedMemberData)
          : options.hangerAngle
        carriedMemberPayload.angle = carriedMemberAngle

        result.carriedMember[memberName] = carriedMemberPayload
      }

      return result
    },
    { carryingMember: {}, carriedMember: {} },
  )

  return payload
}

const filterMultiTrussCarriedMember = (carriedMember, configuration) => {
  const { leftHip, jack, rightHip } = carriedMember
  const multiTrussCarriedMember = {
    leftCenter: [leftHip, jack, null],
    leftRight: [leftHip, null, rightHip],
    centerRight: [null, jack, rightHip],
  }

  return multiTrussCarriedMember[configuration] || [leftHip, jack, rightHip]
}

export const inputToQuery = (state) => {
  const input = getCalcInputState(state)
  const members = selectMembersState(state)

  // eslint-disable-next-line no-unused-vars
  const { configuration, jobID, quantity, ansitpi, hangerType, ...jobSettings } = input.jobSettings
  // eslint-disable-next-line no-unused-vars
  const { country, connectionType } = input.settings

  const isJoistConnection = isJoistConnectionType(connectionType)
  const isMultiTrussConnection = isMultiTrussConnectionType(connectionType)

  const topFlangeOptions = buildTopFlangeOptionsPayload(input.hangerOptions)
  const hangerAngle = buildAnglePayload(input.hangerOptions)

  const { carryingMember, carriedMember } = buildMemberPayload(members, {
    connectionType,
    isMultiTrussConnection,
    hangerAngle,
  })

  const query = {
    style: isHangerTypeConcealed(hangerType) ? HANGER_TYPE.ALL_TYPES : hangerType,
    buildingCode: BUILDING_CODE[country],
    concealed: isHangerTypeConcealed(hangerType) ? CONCEALED_TYPE.YES : CONCEALED_TYPE.ANY,
    fastenerType: 0,
    sort: 0,
    ledger: isMemberTypeLedger(carryingMember.type) ? LEDGER_TYPE.YES : LEDGER_TYPE.NO,
    designInformations: buildDesignInformationsPayload(input.jobSettings),
    filters: FILTERS_QUERY,
    carryingMember,
    carriedMembers: Object.values(carriedMember),
    hangerOptions: { topFlangeOptions },
    ansitpi,
    flushOption: FLUSH_OPTION.TOP,
  }

  delete carryingMember.type

  if (isJoistConnection) {
    delete query.ansitpi
  } else {
    delete query.hangerOptions
    query.flushOption = FLUSH_OPTION.BOTTOM

    if (isMultiTrussConnection)
      query.carriedMembers = filterMultiTrussCarriedMember(carriedMember, configuration)
  }

  const numberFormattedQuery = numberFormatted(query)
  const absFormattedQuery = toAbsoluteValue(numberFormattedQuery)
  const undefinedFormattedQuery = deleteUndefinedField(absFormattedQuery)

  return undefinedFormattedQuery
}

const debouncingCalculation = debounce(({ getState, dispatch }) => {
  // TODO: Move to next release
  // const currentQuery = getResultQuery(store.getState())
  // const updatedQuery = inputToQuery(store.getState)

  // if (!isEqual(currentQuery, updatedQuery)) {
  //   store.dispatch(resultActions.createOutputQuery(updatedQuery))
  // }

  const state = getState()
  const query = inputToQuery(state)
  const isValid = getFormValid(state)

  if (isValid) dispatch(resultActions.createOutputQuery(query))
}, 500)

const createQuery = (store) => (next) => (action) => {
  next(action)

  if (isCalculationAction(action.type)) {
    debouncingCalculation(store)
  }
}

export default createQuery
